import React from 'react';
import PropTypes from 'prop-types';
import {
  Badge, Button, CellText, EmptyState, Table, Tooltip,
} from '@opendate-app/setlist';
import TeamPagination from '../../TeamManager/components/TeamPagination';

const SUBSCRIPTION_STATUS_COLORS = {
  active: 'success',
  canceling: 'warning',
  past_due: 'error',
  unpaid: 'error',
};

function DonationFrequency({ frequency }) {
  if (!frequency) return '—';
  if (frequency.type === 'one_time') return 'One-time';
  if (frequency.type === 'not_recorded') {
    return (
      <Tooltip title="Logged and imported gifts don't record whether they were part of a recurring donation.">
        <span className="text-muted">Not recorded</span>
      </Tooltip>
    );
  }

  return (
    <CellText
      primary={(
        <>
          {frequency.label}{' '}
          <Badge color={SUBSCRIPTION_STATUS_COLORS[frequency.subscription_status] || 'gray'}>
            Subscription {frequency.subscription_status_label}
          </Badge>
        </>
      )}
      supporting={frequency.next_charge_date ? `Next charge ${frequency.next_charge_date}` : undefined}
    />
  );
}

DonationFrequency.propTypes = {
  frequency: PropTypes.shape({
    type: PropTypes.oneOf(['recurring', 'one_time', 'not_recorded']).isRequired,
    label: PropTypes.string,
    subscription_status: PropTypes.string,
    subscription_status_label: PropTypes.string,
    next_charge_date: PropTypes.string,
  }),
};

export default function DonationsTab({
  team,
  donationsHook,
  canLogDonations = false,
  onLogDonation,
  statementHref,
  showSoftCredits = false,
  onAddSoftCredit,
  onEditFairMarketValue,
  afterDonations = null,
}) {
  const { statementYear, setStatementYear } = donationsHook;
  const giftFields = donationsHook.giftFields || [];
  const donationsState = {
    donations: donationsHook.donations,
    loading: donationsHook.loading,
    error: donationsHook.error,
    donationCount: donationsHook.count,
    lastDonationDate: donationsHook.lastDate || '—',
    firstDonationDate: donationsHook.firstDate || '—',
    donationLevel: donationsHook.totalDonated || '—',
    softCreditTotal: donationsHook.softCreditTotal || '$0.00',
    totalContribution: donationsHook.totalContribution || donationsHook.totalDonated || '—',
    receiptsEnabled: donationsHook.receiptsEnabled,
    missingTeamFields: donationsHook.missingTeamFields,
    statementYears: donationsHook.statementYears,
  };

  const columns = [
    {
      key: 'id',
      header: 'Donation ID',
      render: (donation) => (
        <Button as="a" href={`/teams/${team.id}/orders/${donation.id}`} variant="link-color">
          {donation.id}
        </Button>
      ),
    },
    { key: 'name', header: 'Donation Name', primary: true, render: (donation) => donation.name },
    { key: 'created_at', header: 'Donation Date', render: (donation) => donation.date },
    { key: 'amount', header: 'Amount', render: (donation) => donation.amount },
    { key: 'frequency', header: 'Frequency', render: (donation) => <DonationFrequency frequency={donation.frequency} /> },
    { key: 'method', header: 'Method', render: (donation) => donation.method },
    { key: 'reference', header: 'Reference', render: (donation) => donation.reference || '—' },
    { key: 'stage', header: 'Payment Status', render: (donation) => <Badge color="gray">{donation.stage}</Badge> },
    ...giftFields.map((field) => ({
      key: `gift_${field.id}`,
      header: field.name,
      render: (donation) => donation.gift_values?.[field.id] || '—',
    })),
    ...(team?.can_access_fair_market_value ? [{
      key: 'benefits',
      header: 'Benefits / Deductible',
      render: (donation) => {
        const canEdit = canLogDonations && onEditFairMarketValue && donation.fair_market_value_editable;
        const summary = donation.fair_market_value ? (
          <CellText
            primary={`${donation.fair_market_value} received`}
            supporting={[
              donation.deductible_amount && `${donation.deductible_amount} deductible`,
              donation.goods_services_description,
            ].filter(Boolean).join(' · ') || undefined}
          />
        ) : null;
        if (!canEdit) return summary || '—';
        return (
          <>
            {summary}
            <Button variant="link-color" size="sm" onClick={() => onEditFairMarketValue(donation)} data-testid={`edit-fmv-${donation.id}`}>
              {donation.fair_market_value ? 'Edit benefits' : '+ Add benefits'}
            </Button>
          </>
        );
      },
    }] : []),
    ...(showSoftCredits ? [{
      key: 'soft_credits',
      header: 'Soft Credits',
      render: (donation) => (
        <Button variant="link-color" onClick={() => onAddSoftCredit && onAddSoftCredit(donation)}>
          {donation.soft_credit_count > 0
            ? `${donation.soft_credit_attributed} (${donation.soft_credit_count})`
            : '+ Add soft credit'}
        </Button>
      ),
    }] : []),
    {
      key: 'receipt',
      header: 'Download Receipt',
      render: (donation) => (donation.receipt_url ? (
        <Button as="a" href={donation.receipt_url} variant="link-color">Download receipt</Button>
      ) : null),
    },
  ];

  return (
    <div className="cdp-donations-tab py-4">
      {showSoftCredits ? (
        // Contributions summary, mirroring AudienceView:
        // Donation Amount + Soft Credit Amount = Total Contribution.
        <div className="card mb-4">
          <div className="card-body">
            <h2 className="h6 mb-3">Contributions</h2>
            <div className="row g-4 align-items-center">
              <div className="col-md-3 col-sm-6">
                <h6 className="cdp-field-label">Donation Amount</h6>
                <div className="cdp-field-value">{donationsState.donationLevel}</div>
              </div>
              <div className="col-md-3 col-sm-6">
                <h6 className="cdp-field-label">Soft Credit Amount</h6>
                <div className="cdp-field-value">{donationsState.softCreditTotal}</div>
              </div>
              <div className="col-md-3 col-sm-6">
                <h6 className="cdp-field-label">Total Contribution</h6>
                <div className="cdp-field-value fw-bold">{donationsState.totalContribution}</div>
              </div>
              <div className="col-md-3 col-sm-6">
                <h6 className="cdp-field-label">Donation Count</h6>
                <div className="cdp-field-value">
                  {donationsState.donationCount != null ? donationsState.donationCount : '—'}
                </div>
              </div>
            </div>
            <div className="row g-4 mt-4">
              <div className="col-md-3 col-sm-6">
                <h6 className="cdp-field-label">Last Donation Date</h6>
                <div className="cdp-field-value">{donationsState.lastDonationDate || '—'}</div>
              </div>
              <div className="col-md-3 col-sm-6">
                <h6 className="cdp-field-label">First Donation Date</h6>
                <div className="cdp-field-value">{donationsState.firstDonationDate || '—'}</div>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="row g-4 mb-4">
          <div className="col-md-3 col-sm-6">
            <h6 className="cdp-field-label">Donation Count</h6>
            <div className="cdp-field-value">
              {donationsState.donationCount != null ? donationsState.donationCount : '—'}
            </div>
          </div>
          <div className="col-md-3 col-sm-6">
            <h6 className="cdp-field-label">Last Donation Date</h6>
            <div className="cdp-field-value">
              {donationsState.lastDonationDate || '—'}
            </div>
          </div>
          <div className="col-md-3 col-sm-6">
            <h6 className="cdp-field-label">Total Donated</h6>
            <div className="cdp-field-value">{donationsState.donationLevel}</div>
          </div>
          <div className="col-md-3 col-sm-6">
            <h6 className="cdp-field-label">First Donation Date</h6>
            <div className="cdp-field-value">
              {donationsState.firstDonationDate || '—'}
            </div>
          </div>
        </div>
      )}

      {!donationsState.receiptsEnabled && donationsState.missingTeamFields.length > 0 && (
        <div className="alert alert-warning mb-3" role="alert">
          To enable downloadable tax receipts, complete your organization&rsquo;s legal name, EIN, and address in{' '}
          <a href={`/teams/${team.id}/account_details`}>Account Details</a>.
        </div>
      )}

      <div className="card mb-3">
        <div className="card-body p-0">
          <div className="border-bottom px-4 py-3 d-flex justify-content-between align-items-center">
            <h2 className="h6 mb-0">Donations</h2>
            {canLogDonations && (
              <button
                type="button"
                className="btn btn-sm btn-setlist-primary"
                onClick={onLogDonation}
                data-testid="log-donation-button"
              >
                Log Donation
              </button>
            )}
          </div>

          <Table
            columns={columns}
            data={donationsState.donations}
            getRowKey={(donation) => donation.id}
            loading={donationsState.loading}
            loadingLabel="Loading donations..."
            error={donationsState.error || null}
            empty={<EmptyState size="sm" title="No donations yet." />}
          />

          <TeamPagination
            currentPage={donationsHook.page}
            totalPages={donationsHook.totalPages}
            totalItems={donationsHook.totalEntries}
            pageSize={donationsHook.perPage}
            itemLabel="donations"
            onPageChange={(newPage) => {
              if (!donationsHook.loading && newPage >= 1 && newPage <= donationsHook.totalPages) {
                donationsHook.setPage(newPage);
              }
            }}
          />
        </div>
      </div>

      {afterDonations}

      {donationsState.receiptsEnabled && donationsState.statementYears.length > 0 && (
        <div className="card">
          <div className="card-body">
            <h2 className="h6">Annual donation statement</h2>
            <div className="d-flex align-items-center" style={{ gap: '8px' }}>
              <select
                className="custom-select w-auto"
                value={statementYear}
                onChange={(e) => setStatementYear(e.target.value)}
              >
                {donationsState.statementYears.map((y) => (
                  <option key={y} value={String(y)}>{y}</option>
                ))}
              </select>
              <a
                className="btn btn-setlist-primary btn-sm"
                href={statementHref(statementYear)}
              >
                Download
              </a>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

DonationsTab.propTypes = {
  team: PropTypes.object.isRequired,
  donationsHook: PropTypes.object.isRequired,
  canLogDonations: PropTypes.bool,
  onLogDonation: PropTypes.func,
  statementHref: PropTypes.func.isRequired,
  showSoftCredits: PropTypes.bool,
  onAddSoftCredit: PropTypes.func,
  onEditFairMarketValue: PropTypes.func,
  afterDonations: PropTypes.node,
};
